import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography,
  Slider,
  styled,
  Paper,
  Button,
} from '@mui/material';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import MoodBadIcon from '@mui/icons-material/MoodBad';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import MoodIcon from '@mui/icons-material/Mood';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSpring, animated, config } from '@react-spring/web';
import { languages, useLanguage } from '../data/languages';

// Container with dynamic background color based on rating
const FeedbackContainer = styled(animated(Paper))(({ theme, rating }) => {
  // Calculate colors based on rating (1-10)
  // Lower rating = more red, higher rating = more green
  const getGradientColor = () => {
    if (rating <= 5) {
      // Red to yellow gradient for ratings 1-5
      const redIntensity = 255;
      const greenIntensity = Math.round(255 * (rating / 5));
      return `linear-gradient(to bottom, 
        rgba(255, 255, 255, 0.95) 0%, 
        rgba(${redIntensity}, ${greenIntensity}, 100, 0.2) 100%)`;
    } else {
      // Yellow to green gradient for ratings 6-10
      const redIntensity = Math.round(255 * (1 - (rating - 5) / 5));
      const greenIntensity = 255;
      return `linear-gradient(to bottom, 
        rgba(255, 255, 255, 0.95) 0%, 
        rgba(${redIntensity}, ${greenIntensity}, 100, 0.2) 100%)`;
    }
  };

  return {
    padding: theme.spacing(5),
    width: '100%',
    maxWidth: '700px',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    background: getGradientColor(),
    backdropFilter: 'blur(10px)',
    borderRadius: theme.spacing(3),
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    position: 'relative',
    overflow: 'hidden',
    transformOrigin: 'center center',
  };
});

const SliderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(1),
}));

const SubmitButton = styled(Button)(({ theme, disabled }) => ({
  marginTop: theme.spacing(4),
  alignSelf: 'center',
  padding: theme.spacing(1, 4),
  borderRadius: theme.spacing(3),
  fontSize: '1.1rem',
  fontFamily: "'EB Garamond', serif",
  backgroundColor: disabled ? theme.palette.grey[400] : theme.palette.primary.main,
  color: '#fff',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: disabled ? theme.palette.grey[400] : theme.palette.primary.dark,
    transform: disabled ? 'none' : 'translateY(-2px)',
    boxShadow: disabled ? 'none' : '0 6px 16px rgba(0,0,0,0.2)',
  },
}));

const ThankYouMessage = styled(animated(Box))(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(3),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  width: '100%',
  maxWidth: '700px',
}));

export default function FeedbackView({ question, onRatingChange, initialStressLevel, finalStressLevel }) {
  const [rating, setRating] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [stressChange, setStressChange] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);
  const currentLang = useLanguage(); // 获取当前语言
  const t = languages[currentLang] || languages.en; // 获取翻译文本
  
  // Initialize from props if finalStressLevel is already set
  useEffect(() => {
    if (finalStressLevel !== null) {
      setRating(finalStressLevel);
      setSubmitted(true);
      setStressChange(initialStressLevel ? initialStressLevel - finalStressLevel : 0);
      setHasInteracted(true);
    }
  }, [finalStressLevel, initialStressLevel]);
  
  // Spring animation for the feedback form
  const springProps = useSpring({
    opacity: submitted ? 0 : 1,
    scale: submitted ? 0 : 1,
    config: { 
      duration: 1000,
    }
  });
  
  // Spring animation for the thank you message
  const thankYouSpring = useSpring({
    from: { 
      opacity: 0, 
      scale: 0.8,
      y: 20
    },
    to: { 
      opacity: submitted ? 1 : 0, 
      scale: submitted ? 1 : 0.8,
      y: submitted ? 0 : 20
    },
    config: config.wobbly,
    delay: 800
  });
  
  // Get emoji based on stress change or current rating
  const getEmoji = () => {
    if (!submitted) {
      // Default emoji before submission based on current slider position
      if (!rating) return <SentimentSatisfiedIcon sx={{ fontSize: 64, color: '#ffd06b' }} />;
      if (rating <= 2) return <MoodBadIcon sx={{ fontSize: 64, color: '#ff6b6b' }} />;
      if (rating <= 4) return <SentimentDissatisfiedIcon sx={{ fontSize: 64, color: '#ffa06b' }} />;
      if (rating <= 6) return <SentimentSatisfiedIcon sx={{ fontSize: 64, color: '#ffd06b' }} />;
      if (rating <= 8) return <SentimentSatisfiedAltIcon sx={{ fontSize: 64, color: '#9eff6b' }} />;
      return <MoodIcon sx={{ fontSize: 64, color: '#6bffb5' }} />;
    }
    
    // After submission, based on calculated stress change
    if (stressChange >= 4) return <MoodIcon sx={{ fontSize: 64, color: '#6bffb5' }} />; // Major improvement
    if (stressChange >= 2) return <SentimentSatisfiedAltIcon sx={{ fontSize: 64, color: '#9eff6b' }} />; // Good improvement
    if (stressChange > 0) return <SentimentSatisfiedIcon sx={{ fontSize: 64, color: '#ffd06b' }} />; // Slight improvement
    if (stressChange === 0) return <SentimentSatisfiedIcon sx={{ fontSize: 64, color: '#ffd06b' }} />; // No change
    if (stressChange >= -2) return <SentimentDissatisfiedIcon sx={{ fontSize: 64, color: '#ffa06b' }} />; // Slight worsening
    return <MoodBadIcon sx={{ fontSize: 64, color: '#ff6b6b' }} />; // Major worsening
  };
  
  const handleChange = (event, newValue) => {
    setRating(newValue);
    setHasInteracted(true);
  };

  const handleSubmit = () => {
    if (!rating) return; // Don't submit if no rating selected
    
    // Calculate stress change at submission time
    const calculatedStressChange = initialStressLevel ? initialStressLevel - rating : 0;
    setStressChange(calculatedStressChange);
    
    // Pass the rating back to DetailNPC to update finalStressLevel
    if (onRatingChange) {
      onRatingChange(rating);
    }
    
    setSubmitted(true);
  };

  return (
    <>
      {/* Feedback form with fade-out animation */}
      <FeedbackContainer 
        elevation={3}
        rating={rating || 5} // Use 5 for styling if no rating selected
        style={{
          opacity: springProps.opacity,
          transform: springProps.scale.to(scale => `scale(${scale})`),
          display: springProps.opacity.to(o => o === 0 ? 'none' : 'flex'),
        }}
      >
        <Typography variant="h5" sx={{ 
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: '1.5rem',
          fontStyle: 'italic',
          color: 'rgba(0, 0, 0, 0.7)',
          textAlign: 'center',
          mb: 2,
        }}>
          {t.feedbackView.yourQuestion} "{question}"
        </Typography>
        
        <Typography variant="h6" sx={{ 
          fontFamily: "'EB Garamond', serif",
          fontSize: '1.4rem',
          textAlign: 'center',
          mt: 2,
        }}>
          {initialStressLevel 
            ? t.feedbackView.initialConfidence.replace('{level}', initialStressLevel)
            : t.feedbackView.howFeel}
        </Typography>
        
        <SliderContainer>
          <SentimentVeryDissatisfiedIcon color="error" sx={{ fontSize: 32 }} />
          <Slider
            value={rating || 0}
            onChange={handleChange}
            aria-labelledby="feedback-slider"
            valueLabelDisplay="auto"
            step={1}
            marks
            min={1}
            max={10}
            disabled={submitted}
            sx={{
              color: (theme) => theme.palette.primary.main,
              '& .MuiSlider-thumb': {
                height: 28,
                width: 28,
              },
              '& .MuiSlider-valueLabel': {
                backgroundColor: (theme) => theme.palette.primary.main,
                fontSize: '1rem',
                padding: '6px 12px',
              },
              '& .MuiSlider-track': {
                height: 8,
              },
              '& .MuiSlider-rail': {
                height: 8,
              }
            }}
          />
          <SentimentVerySatisfiedIcon color="success" sx={{ fontSize: 32 }} />
        </SliderContainer>
        
        <SubmitButton 
          variant="contained" 
          onClick={handleSubmit}
          startIcon={<CheckCircleIcon />}
          disabled={!hasInteracted || !rating}
        >
          {t.feedbackView.submitFeedback}
        </SubmitButton>
        
        {!hasInteracted && (
          <Typography 
            variant="body2" 
            sx={{ 
              textAlign: 'center', 
              color: 'text.secondary',
              fontStyle: 'italic',
              mt: 2 
            }}
          >
            {t.feedbackView.pleaseSelect}
          </Typography>
        )}
      </FeedbackContainer>

      {/* Thank you message with pop-in animation */}
      <ThankYouMessage
        style={{
          opacity: thankYouSpring.opacity,
          transform: thankYouSpring.scale.to(s => `scale(${s})`).to(scale => 
            thankYouSpring.y.to(y => `translate3d(0,${y}px,0) scale(${scale})`)
          ),
          display: thankYouSpring.opacity.to(o => o === 0 ? 'none' : 'flex'),
        }}
      >
        {getEmoji()}
        <Typography variant="h4" sx={{ 
          fontFamily: "'Recoleta', serif",
          mb: 2,
          mt: 2
        }}>
          {t.feedbackView.thankYou}
        </Typography>
        <Typography variant="body1" sx={{ 
          fontFamily: "'EB Garamond', serif",
          fontSize: '1.2rem',
          maxWidth: '80%',
          mb: 2
        }}>
          {initialStressLevel ? (
            stressChange > 0 
              ? t.feedbackView.improvementMessage.replace('{change}', stressChange)
              : stressChange === 0 
                ? t.feedbackView.noChangeMessage
                : t.feedbackView.decreaseMessage
          ) : (
            rating < 5 
              ? t.feedbackView.generalStressMessage
              : t.feedbackView.generalGoodMessage
          )}
        </Typography>
      </ThankYouMessage>
    </>
  );
} 